'use client'

import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { displayedLeverage, parseLeverageDraft } from '@/lib/papertrade/leverage'
import { cn } from '@/lib/utils'
import { useAccountSettings, useSetLeverage } from '@/service/account-settings.hook'

interface LeverageChipProps {
  accountId: string
  symbol: string
  maxLeverage?: string
}

/** Per-symbol leverage of a Papertrade account. Renders nothing when the venue reports none. */
export function LeverageChip({ accountId, symbol, maxLeverage }: LeverageChipProps) {
  const t = useTranslations('orderForm')
  const { data: settings } = useAccountSettings(accountId)
  const setLeverage = useSetLeverage()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('1')

  const current = displayedLeverage(settings, symbol, maxLeverage)
  if (current === null) return null
  const max = Math.floor(Number(maxLeverage))

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (next) {
      setDraft(String(current))
      setLeverage.reset()
    }
  }

  const parsed = parseLeverageDraft(draft, max)
  const sliderValue = parsed ?? 1
  const fillPct = max > 1 ? ((sliderValue - 1) / (max - 1)) * 100 : 100

  const handleBlur = () => {
    if (parsed !== null) setDraft(String(parsed))
  }

  const handleSave = () => {
    if (parsed === null) return
    setLeverage.mutate(
      { accountId, instrument: symbol, leverage: parsed },
      { onSuccess: () => setOpen(false) }
    )
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          tabIndex={-1}
          className="border-foreground/10 text-foreground/60 hover:text-foreground cursor-pointer border px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap"
        >
          {t('leverageChip', { leverage: current })}
        </button>
      </PopoverTrigger>
      <PopoverContent className="flex flex-col gap-2" align="start">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium">{t('leverageLabel')}</span>
          <input
            type="number"
            min={1}
            max={max}
            step={1}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={handleBlur}
            aria-label={t('leverageLabel')}
            className="border-foreground/10 focus:border-foreground/30 h-7 w-16 [appearance:textfield] border bg-transparent px-2 text-right text-sm outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
        </div>
        {/* Theme tokens only: --primary flips per theme (white on dark, ink on light, cyan on Blue). */}
        <input
          type="range"
          min={1}
          max={max}
          step={1}
          value={sliderValue}
          onChange={(e) => setDraft(e.target.value)}
          aria-label={t('leverageLabel')}
          style={{ '--fill': `${fillPct}%` } as React.CSSProperties}
          className={cn(
            'h-1.5 w-full cursor-pointer appearance-none rounded-full bg-transparent',
            '[&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-[linear-gradient(to_right,var(--primary)_var(--fill),color-mix(in_srgb,var(--foreground)_10%,transparent)_var(--fill))]',
            '[&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-background [&::-webkit-slider-thumb]:-mt-[5px] [&::-webkit-slider-thumb]:size-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2',
            '[&::-moz-range-track]:bg-foreground/10 [&::-moz-range-track]:h-1.5 [&::-moz-range-track]:rounded-full',
            '[&::-moz-range-progress]:bg-primary [&::-moz-range-progress]:h-1.5 [&::-moz-range-progress]:rounded-full',
            '[&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-background [&::-moz-range-thumb]:size-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2'
          )}
        />
        <div className="text-foreground/35 flex justify-between text-[10px]">
          <span>1x</span>
          <span>{max}x</span>
        </div>
        <p className="text-muted-foreground text-xs">{t('leverageAppliesNote')}</p>
        {setLeverage.error && (
          <p className="text-destructive text-xs">{setLeverage.error.message}</p>
        )}
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={setLeverage.isPending || parsed === null || parsed === current}
        >
          {setLeverage.isPending ? t('leverageSaving') : t('leverageSave')}
        </Button>
      </PopoverContent>
    </Popover>
  )
}
